import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Download, RefreshCw, Link2, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import LinkPoLineDialog from '../components/purchasing/LinkPoLineDialog';

const TILES = [
  ['unlinked_po_lines', 'Unlinked PO lines', 'unlinked'],
  ['receipt_mismatches', 'Receipts not in stock', 'mismatch'],
  ['broken_po_item_refs', 'PO lines → deleted item', 'unlinked'],
  ['items_missing_required', 'Items missing required fields', 'fields'],
  ['broken_bom_lines', 'BOM lines → missing item', 'bom'],
];
const th = 'text-left text-[10px] uppercase tracking-wider text-slate-500 px-2 py-1.5';
const td = 'px-2 py-1.5 text-sm border-t border-slate-100';
const PoLink = ({ r }) => <Link to={`/purchasing/pos/${r.po_id}`} className="text-blue-700 hover:underline mono">{r.po_number}</Link>;
const Empty = ({ t }) => <div className="p-8 text-center text-sm text-emerald-700" data-testid={`di-empty-${t}`}>All clear. Nothing to fix here.</div>;

const Table = ({ head, rows, render, testId }) => (
  <div className="overflow-x-auto" data-testid={testId}>
    <table className="w-full"><thead><tr>{head.map(h => <th key={h} className={th}>{h}</th>)}</tr></thead>
      <tbody>{rows.map(render)}</tbody></table>
  </div>
);

export default function DataIntegrityPage() {
  const { api } = useAuth();
  const [d, setD] = useState(null);
  const [tab, setTab] = useState('unlinked');
  const [link, setLink] = useState(null);
  const [dg, setDg] = useState(null);
  const { user } = useAuth();
  const loadDg = useCallback(() => api().get('/data-integrity/digest').then(r => setDg(r.data)).catch(() => {}), [api]);
  useEffect(() => { loadDg(); }, [loadDg]);
  const sendNow = async () => {
    try { const r = await api().post('/data-integrity/digest/send'); toast[r.data.integrity_digest === 'sent' ? 'success' : 'error'](r.data.integrity_digest === 'sent' ? `Sent to ${r.data.recipients} recipient(s)` : `Not sent: ${r.data.detail || r.data.integrity_digest}`); loadDg(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
  };
  const load = useCallback(() => api().get('/data-integrity/report').then(r => setD(r.data)).catch(() => toast.error('Failed to load report')), [api]);
  useEffect(() => { load(); }, [load]);
  const csv = async () => {
    try {
      const r = await api().get('/data-integrity/report.csv', { responseType: 'blob' });
      const url = URL.createObjectURL(r.data); const a = document.createElement('a'); a.href = url; a.download = `data-integrity-${new Date().toISOString().slice(0, 10)}.csv`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };
  if (!d) return <div className="p-6 text-slate-400">Loading…</div>;
  const s = d.summary;
  return (
    <div className="space-y-5" data-testid="data-integrity-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><ShieldCheck className="w-6 h-6" /> Data Integrity</h1>
          <p className="text-sm text-slate-500">Every PO line must point to an inventory item, and every receipt must reach stock. Fix the gaps below.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="rounded-sm" onClick={load} data-testid="di-refresh-btn"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh</Button>
          <Button variant="outline" size="sm" className="rounded-sm" onClick={csv} data-testid="di-export-csv-btn"><Download className="w-3.5 h-3.5 mr-1" /> Export CSV</Button>
        </div>
      </div>
      {dg && (
        <div className="flex items-center justify-between gap-3 flex-wrap rounded-sm border border-slate-200 bg-white px-4 py-2.5 text-sm" data-testid="di-digest-card">
          <span><Mail className="w-4 h-4 inline mr-1 text-slate-400" /><b>Weekly email</b> · {dg.schedule} · to {dg.recipients?.length || 0} recipient(s){dg.recipients?.length ? ` (${dg.recipients.join(', ')})` : ''}
            <span className="text-slate-500" data-testid="di-digest-last"> · last sent {dg.last_sent_at ? new Date(dg.last_sent_at).toLocaleString() : 'never'}{dg.last_status && dg.last_status !== 'sent' ? ` (last attempt failed: ${dg.last_detail || dg.last_status})` : ''}</span></span>
          {user?.role === 'Admin' && <Button size="sm" variant="outline" className="rounded-sm" onClick={sendNow} data-testid="di-digest-send-btn"><Mail className="w-3.5 h-3.5 mr-1" /> Send now</Button>}
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {TILES.map(([k, label, t]) => (
          <button key={k} type="button" onClick={() => setTab(t)} className={`text-left border rounded-sm p-3 bg-white transition-colors hover:bg-slate-50 ${s[k] ? 'border-red-300' : 'border-emerald-200'}`} data-testid={`di-tile-${k}`}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
            <div className={`text-2xl font-bold mono ${s[k] ? 'text-red-700' : 'text-emerald-700'}`} data-testid={`di-count-${k}`}>{s[k]}</div>
            {k === 'unlinked_po_lines' && s.unlinked_received > 0 && <div className="text-[11px] text-red-700">{s.unlinked_received} already received</div>}
          </button>
        ))}
      </div>
      <Card className="industrial-card"><CardContent className="p-0">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="m-3">
            <TabsTrigger value="unlinked" data-testid="di-tab-unlinked">PO lines</TabsTrigger>
            <TabsTrigger value="mismatch" data-testid="di-tab-mismatch">Receipts</TabsTrigger>
            <TabsTrigger value="fields" data-testid="di-tab-fields">Item key fields</TabsTrigger>
            <TabsTrigger value="bom" data-testid="di-tab-bom">BOM lines</TabsTrigger>
            <TabsTrigger value="map" data-testid="di-tab-map">Field map</TabsTrigger>
          </TabsList>
          <TabsContent value="unlinked">
            {d.unlinked_po_lines.length + d.broken_po_item_refs.length === 0 ? <Empty t="unlinked" /> : (
              <Table testId="di-unlinked-table" head={['PO', 'Status', 'Vendor', 'Description', 'Vendor P/N', 'Qty', 'Received', '']} rows={[...d.unlinked_po_lines, ...d.broken_po_item_refs]}
                render={(r, i) => (
                  <tr key={r.line_id} data-testid={`di-unlinked-row-${i}`}>
                    <td className={td}><PoLink r={r} /></td><td className={td}>{r.status}</td><td className={td}>{r.vendor_name}</td>
                    <td className={td}>{r.description}{r.item_id && <span className="text-[11px] text-red-700 ml-1">(item {r.item_sku || r.item_id} was deleted)</span>}</td>
                    <td className={`${td} mono`}>{r.vendor_part_number || '—'}</td><td className={`${td} mono`}>{r.quantity}</td>
                    <td className={`${td} mono ${r.received_qty > 0 ? 'text-red-700 font-semibold' : ''}`}>{r.received_qty}</td>
                    <td className={td}>{!r.item_id && <Button size="sm" variant="outline" className="h-7 rounded-sm" onClick={() => setLink(r)} data-testid={`di-link-btn-${i}`}><Link2 className="w-3 h-3 mr-1" /> Link item</Button>}</td>
                  </tr>
                )} />
            )}
          </TabsContent>
          <TabsContent value="mismatch">
            {d.receipt_mismatches.length === 0 ? <Empty t="mismatch" /> : (
              <Table testId="di-mismatch-table" head={['PO', 'Status', 'Item', 'Received on PO', 'Posted to stock', 'Difference']} rows={d.receipt_mismatches}
                render={(r, i) => (
                  <tr key={`${r.po_id}-${r.item_id}`} data-testid={`di-mismatch-row-${i}`}>
                    <td className={td}><PoLink r={r} /></td><td className={td}>{r.status}</td>
                    <td className={td}><Link to={`/items/view/${r.item_id}`} className="text-blue-700 hover:underline mono">{r.item_sku}</Link></td>
                    <td className={`${td} mono`}>{r.received_qty}</td><td className={`${td} mono`}>{r.posted_qty}</td><td className={`${td} mono text-red-700 font-semibold`}>{r.difference}</td>
                  </tr>
                )} />
            )}
          </TabsContent>
          <TabsContent value="fields">
            {d.items_missing_fields.length === 0 ? <Empty t="fields" /> : (
              <Table testId="di-fields-table" head={['SKU', 'Name', 'Type', 'Missing required', 'Missing recommended']} rows={d.items_missing_fields}
                render={(r, i) => (
                  <tr key={r.item_id} data-testid={`di-fields-row-${i}`}>
                    <td className={td}><Link to={`/items/view/${r.item_id}`} className="text-blue-700 hover:underline mono">{r.item_sku}</Link></td>
                    <td className={td}>{r.item_name}</td><td className={td}>{r.item_type}</td>
                    <td className={`${td} text-red-700`}>{r.missing_required.join(', ') || '—'}</td>
                    <td className={`${td} text-amber-700`}>{r.missing_recommended.join(', ') || '—'}</td>
                  </tr>
                )} />
            )}
          </TabsContent>
          <TabsContent value="bom">
            {d.broken_bom_lines.length === 0 ? <Empty t="bom" /> : (
              <Table testId="di-bom-table" head={['BOM', 'Status', 'Component SKU', 'Qty / unit']} rows={d.broken_bom_lines}
                render={(r, i) => (
                  <tr key={`${r.bom_id}-${i}`} data-testid={`di-bom-row-${i}`}>
                    <td className={td}><Link to={`/boms/view/${r.bom_id}`} className="text-blue-700 hover:underline">{r.bom}</Link></td><td className={td}>{r.status}</td>
                    <td className={`${td} mono text-red-700`}>{r.component_sku}</td><td className={`${td} mono`}>{r.quantity_per_unit}</td>
                  </tr>
                )} />
            )}
          </TabsContent>
          <TabsContent value="map"><FieldMap fields={d.key_fields} /></TabsContent>
        </Tabs>
      </CardContent></Card>
      {link && <LinkPoLineDialog api={api} open={!!link} onOpenChange={(o) => { if (!o) setLink(null); }} poId={link.po_id} poNumber={link.po_number} vendorId={link.vendor_id}
        line={{ id: link.line_id, description: link.description, vendor_part_number: link.vendor_part_number, quantity: link.quantity, received_qty: link.received_qty, unit_cost: link.unit_cost }} onLinked={() => load()} />}
    </div>
  );
}

const DEPS = [
  ['PO line → Item', 'item_id', 'Receiving adds stock, sets last PO cost, counts as "on order", adds price history'],
  ['Item → Preferred vendor', 'preferred_vendor_id', 'Default vendor for reorders, price breaks, vendor scorecard'],
  ['Item → BOM lines', 'item_sku = component_sku', 'Where-used, BOM costing, work-order material demand'],
  ['Item → Transactions', 'item_sku', 'Stock on hand history, variance and audits'],
  ['Item → Receipts', 'item_id + PO number', 'Landed cost, lot tracking, PO receipt reconciliation'],
  ['Item → Price breaks', 'item_id + vendor_id', 'Auto unit price on PO lines by quantity'],
  ['Item → R&D draft BOMs', 'component_sku', 'Promoted to released BOMs'],
  ['Item → Sales / Work orders', 'item_sku / product_sku / materials', 'Demand and consumption'],
];

const FieldMap = ({ fields }) => (
  <div className="p-4 grid md:grid-cols-2 gap-6" data-testid="di-field-map">
    <div>
      <p className="text-sm font-semibold mb-2">Item key fields checked</p>
      {fields.map(f => <div key={f.key} className="flex justify-between text-sm border-t border-slate-100 py-1"><span>{f.label} <span className="mono text-[11px] text-slate-400">{f.key}</span></span><span className={f.level === 'required' ? 'text-red-700 text-xs font-semibold' : 'text-amber-700 text-xs'}>{f.level}</span></div>)}
    </div>
    <div>
      <p className="text-sm font-semibold mb-2">How records connect to each part</p>
      {DEPS.map(([a, k, why]) => <div key={a} className="text-sm border-t border-slate-100 py-1.5"><div className="font-medium">{a} <span className="mono text-[11px] text-slate-400">{k}</span></div><div className="text-xs text-slate-500">{why}</div></div>)}
    </div>
  </div>
);
